/**
 * Под какими адресами браузер виден снаружи России.
 *
 * Зачем. Решение владельца 18.09.2026: с включённым VPN приложением пользоваться нельзя вовсе.
 * Android и iOS узнают о VPN от системы, браузеру такого не дают. А наш сервер VPN почти не видит:
 * Happ, AdGuard VPN и им подобные пускают российские сайты мимо туннеля, и к нам человек приходит
 * со своим домашним адресом. Зарубежный сервер при этом видит выход VPN.
 *
 * Как. Видеозвонки в браузере узнают свой внешний адрес у общедоступных серверов Google и
 * Cloudflare — те отвечают, под каким адресом видят браузер. Спросить их может любая страница без
 * разрешений и без камеры. Адреса уходят нашему серверу (`vpnCheck.ts`), и он сверяет их с базой
 * стран. Проверено вживую 18.09.2026: под AdGuard VPN сервер Google назвал амстердамский выход,
 * а наш API в ту же минуту видел домашний российский адрес.
 *
 * Не вышло (сервера недоступны, браузер без WebRTC, таймаут) — адресов просто нет, и решает только
 * адрес самого запроса. Ошибки здесь не бывает: проверка не должна закрыть приложение человеку без
 * VPN из-за собственной поломки.
 */

const STUN_SERVERS = ['stun:stun.l.google.com:19302', 'stun:stun.cloudflare.com:3478'];

/** Сколько ждём ответов. Обычно оба приходят за доли секунды. */
export const GATHER_TIMEOUT_MS = 2_500;

/** Внешние адреса браузера — без повторов, возможно пустой список. */
export function gatherPublicAddresses(timeoutMs = GATHER_TIMEOUT_MS): Promise<string[]> {
  const Peer = globalThis.RTCPeerConnection as typeof RTCPeerConnection | undefined;
  if (typeof Peer !== 'function') return Promise.resolve([]);

  let peer: RTCPeerConnection;
  try {
    peer = new Peer({ iceServers: [{ urls: STUN_SERVERS }] });
  } catch {
    return Promise.resolve([]);
  }

  const found = new Set<string>();
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      try {
        peer.close();
      } catch {
        // Закрыть не вышло — соединение и так никуда не ведёт.
      }
      resolve([...found]);
    };
    const timer = window.setTimeout(finish, timeoutMs);

    peer.onicecandidate = (event) => {
      if (event.candidate === null) {
        finish();
        return;
      }
      const address = reflexiveAddress(event.candidate.candidate);
      if (address !== null) found.add(address);
    };
    peer.onicegatheringstatechange = () => {
      if (peer.iceGatheringState === 'complete') finish();
    };

    try {
      // Без канала браузер не начнёт собирать адреса вовсе.
      peer.createDataChannel('vpn-check');
      void peer
        .createOffer()
        .then((offer) => peer.setLocalDescription(offer))
        .catch(finish);
    } catch {
      finish();
    }
  });
}

/**
 * Адрес из строки кандидата, если это адрес «глазами внешнего сервера» (`typ srflx`).
 *
 * Строка устроена так: `candidate:842163049 1 udp 1677729535 146.70.46.2 61234 typ srflx …` —
 * пятое поле адрес, после `typ` вид. Локальные адреса (`typ host`) браузеры прячут за именами
 * `*.local`, и они нам не нужны: смысл ровно в том, что видит сервер снаружи.
 */
export function reflexiveAddress(candidate: string): string | null {
  const parts = candidate.trim().split(/\s+/);
  const typ = parts.indexOf('typ');
  if (typ < 0 || parts[typ + 1] !== 'srflx') return null;
  const address = parts[4] ?? '';
  return /^[0-9a-f.:]+$/i.test(address) ? address : null;
}
